import { getRefId, makeHash } from "../utils/utils"
import uniq from "lodash/uniq"
import { findAiEndDate, getTimeUtc } from "../utils/dates"
import { isBefore } from "date-fns/isBefore"
import { toZonedTime } from "date-fns-tz"
import { v4 as uniqid } from "uuid"
import { getPersonName } from "../utils/utils"
import { addDays, endOfDay, isAfter } from "date-fns"
import { getAllActionItems } from "./planTree"
import { getBoardAction, getSection, getSections, getTaskSectionId, isTaskInSection } from "./sections"

export function getAiCode(ai) {
    if (!ai) {
        return ""
    }
    if (ai.code) {
        return ai.code
    }
    if (ai.createdAt) {
        const st = new Date(ai.createdAt).getTime().toString()
        return st.substring(st.length - 4, st.length)
    }
    return ""
}

export const formatProcessDuration = (ai) => {
    const dv = Math.floor((ai.estimatedDuration || 0) / 8)
    const days = ai.estimatedDuration ? dv : null
    const hours = ai.estimatedDuration ? ai.estimatedDuration % 8 : null

    let d = days ? `${days} day${days === 1 ? "" : "s"}` : ""
    let h = hours ? `${hours} hour${hours === 1 ? "" : "s"}` : ""

    return d + " " + h
}

export const formatAiTags = (ai, mission) => {
    // The task's section is listed with its tags (it used to sit in ai.tags); section ids
    // still in ai.tags are stale
    const sectionId = getTaskSectionId(mission, ai)
    const tags = [...(ai?.tags || []).filter((t) => !getSection(mission, t)), ...(sectionId ? [sectionId] : [])]

    if (!tags.length) {
        return ""
    }
    const allActionItems = mission ? getAllActionItems(mission) : []

    const f = tags
        .map((t) => {
            const fst = getSection(mission, t)

            if (!fst) {
                const myAi = allActionItems.find((a) => a._id === t)
                if (myAi) {
                    return `${getAiCode(myAi)} ${myAi.title || "Task with no title..."}`
                }

                if (t.replaceAll(" ", "").length > 20) {
                    return null
                }
                return t
            } else {
                return fst?.title
            }
        })
        ?.filter((t) => t !== null)

    return f
}

/**
 * The project's "Auto assign to me": while its creator is the only member,
 * a new task with nobody on it goes to them (as the boards do).
 *
 * @param mission {Object}
 * @param personId {string} who is creating the task
 * @param data {Object} the new task's fields
 * @return {Object}
 */
export function withAutoAssign(mission, personId, data) {
    const solo = mission?.autoAssign && (mission.people || []).filter((p) => p?.permission < 3).length === 1

    if (!solo || !personId || data?.people?.length) {
        return data
    }

    return { ...data, people: [personId], seen: [{ by: personId, when: getTimeUtc(new Date()) }] }
}

// Lives in utils/planTree (a leaf module, so no import cycles); kept here for existing imports
export { getAllActionItems }

export function complexityClass(ai) {
    const cm = ai.complexityMultiplier

    if (!cm) {
        return "x-cm"
    }

    return cm < 8 ? "x-cm x-cm-1" : cm >= 8 && cm <= 14 ? "x-cm x-cm-2" : cm > 14 ? "x-cm x-cm-3" : "x-cm"
}

// The board's catch-all action (utils/sections); kept for existing imports
export function getSimpleAction(mission) {
    return getBoardAction(mission)
}

export function actionItemPermissions({ actionItem, mission, app, ignoreDone }) {
    if (!actionItem) {
        return {
            canDelete: false,
            canAdmin: false,
            canUpdate: false,
            canInvite: false,
        }
    }

    const done = actionItem?.status === "done"
    const aboutMeOnMission = mission?.people?.find((p) => getRefId(p) === app.state.person._id)

    if (!aboutMeOnMission) {
        return {
            canDelete: false,
            canAdmin: false,
            canUpdate: false,
            canInvite: false,
        }
    }

    const isCreator = getRefId(aboutMeOnMission) === getRefId(actionItem.createdBy)

    const amIinIt = actionItem.people?.find((aper) => getRefId(aboutMeOnMission) === getRefId(aper))

    const canAdmin =
        isCreator ||
        aboutMeOnMission?.permission === 2 ||
        (aboutMeOnMission?.permission === 1 && aboutMeOnMission?.isProjectManager)
    // Clients (observers) read the task document and its history but never change it:
    // on a task they only do sign-offs and change request approvals. The API refuses too.
    const isObserver = aboutMeOnMission?.permission === 3

    if (ignoreDone) {
        return {
            canDelete: canAdmin,
            canAdmin: canAdmin,
            canUpdate: amIinIt || canAdmin,
            canEditDoc: Boolean(amIinIt || canAdmin) && !isObserver,
            canInvite: canAdmin,
            canChangeStatus: canAdmin || amIinIt,
        }
    }

    return {
        canDelete: canAdmin,
        canAdmin: canAdmin && !done,
        canUpdate: (amIinIt || canAdmin) && !done,
        canEditDoc: Boolean(amIinIt || canAdmin) && !done && !isObserver,
        canInvite: canAdmin && !done,
        canChangeStatus: canAdmin || amIinIt,
    }
}

export function isOverdue(ai, mission) {
    if (!ai) {
        return
    }
    if (ai.status === "done") return false
    const ed = findAiEndDate(ai)

    if (ai.dueDate || ai.endDate) {
        return isBefore(toZonedTime(ai.dueDate || ai.endDate), new Date())
    } else if (ed) {
        return isBefore(ed, new Date())
    }
    return false
}

export function getSuggestions({ mission, ai, hasWeek }) {
    const allAi = getAllActionItems(mission).filter((a) => !!a.title && a._id !== ai?._id && (hasWeek ? a.week : true))
    const aiIds = Object.fromEntries(allAi.map((item) => [item._id, item]))
    const allSections =
        mission.projectType === "simple" || mission.projectType === "kanban"
            ? getSections(mission).filter((st) => st.title)
            : []
    const stIds = Object.fromEntries((allSections || []).map((item) => [item._id, item]))

    let allTags = []

    allAi.forEach((a, i) => {
        allTags = [...allTags, ...(a.tags || [])] || []
    })

    allTags = allTags.filter((t) => {
        return !aiIds[t] && !stIds[t] && !ai?.tags?.includes(t)
    })

    return [...(mission.taskTags || []), ...allSections, ...uniq(allTags), ...allAi].map((obj, i) => {
        const dc = getAiCode(obj)

        return {
            obj: obj,
            type: obj.actionId ? "ai" : obj?._id ? "st" : "tag",
            name: obj.actionId ? (dc ? dc + " " : "") + obj.title : obj?.title || obj,
            id: obj._id || i,
        }
    })
}

export function aiDates(ai) {
    if (ai.startDate && ai.endDate && ai.startDate < ai.endDate) {
        return {
            startDate: ai.startDate,
            endDate: ai.endDate,
        }
    }
    if (!ai.week) {
        return null
    }

    const startDay = addDays(toZonedTime(ai.week), Math.floor((ai.startHour ?? 0) / 8))
    const days = ai.estimatedDuration ? Math.ceil(ai.estimatedDuration / 8) - 1 : 1
    let endDay = endOfDay(addDays(startDay, days))

    if (ai.endDate && isAfter(toZonedTime(ai.endDate), startDay)) {
        endDay = toZonedTime(ai.endDate)
    }

    return {
        startDate: startDay,
        endDate: endDay,
    }
}

export function filterSimpleTasks({ filter, mission }) {
    const allActionItems = getAllActionItems(mission)
    const people = makeHash(
        mission.people.filter((p) => p.ref).map((p) => p.ref),
        "_id",
    )
    if (!filter.obj && filter.value) {
        return allActionItems.filter((ai, i) => {
            return (
                filter.value === "" ||
                !filter.value ||
                ai.title?.toLowerCase().includes(filter.value?.toLowerCase()) ||
                ai.tags?.map((t) => t?.toLowerCase()).includes(filter.value.toLowerCase())
            )
        })
    } else if (filter?.obj?.type === "ai") {
        const el = document.getElementById("x-tc-" + filter?.obj.id)
        if (el) {
            el.scrollIntoView({
                behaviour: "smooth",
                block: "center",
            })
        }
        return [filter.obj.obj]
    } else if (filter?.obj?.type === "st") {
        const fit = allActionItems.filter((ai, i) => {
            return isTaskInSection(mission, ai, filter.obj.obj._id)
        })

        return fit
    } else if (filter?.obj?.type === "tag" && filter.value) {
        const fitTags = allActionItems.filter((ai, i) => {
            return ai.tags?.map((t) => t?.toLowerCase()).includes(filter.value.toLowerCase())
        })
        return fitTags
    } else if (filter?.obj?.type === "person" && filter.value) {
        const fitTags = allActionItems.filter((ai, i) => {
            const x = ai.people?.map((p) => getPersonName(people[getRefId(p)])?.toLowerCase())

            return x.includes(filter.value.toLowerCase())
        })

        return fitTags
    }

    return []
}
